import { useTranslation } from 'react-i18next';
import { Dropdown, DropdownTriggerButton, DropdownContent, DropDownSelectToClose } from '@/components/Dropdown';
import { PageContentCard } from '@/components/layout/PageContentCard';
import type { Step } from '../types';

interface DeviceAssignmentMobileProps {
  steps: Step[];
  activeStep: Step['id'];
  onStepChange: (stepId: Step['id']) => void;
  isStepDisabled: (stepId: Step['id']) => boolean;
  renderStepContent: (stepId: Step['id']) => React.ReactNode;
}

export function DeviceAssignmentMobile({
  steps,
  activeStep,
  onStepChange,
  isStepDisabled,
  renderStepContent,
}: DeviceAssignmentMobileProps) {
  return (
    <div className="space-y-4 flex-1 min-h-0 flex flex-col sm:hidden">
      <Dropdown>
        <DropdownTriggerButton className="w-full justify-between">
          <div className="flex flex-col items-start">
            <HeaderNavContent step={steps.find((s) => s.id === activeStep) as Step} />
          </div>
          <svg
            className="shrink-0 size-4"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </DropdownTriggerButton>
        <DropdownContent>
          {steps.map((step) => {
            const disabled = isStepDisabled(step.id);
            const isActive = activeStep === step.id;

            return (
              <DropDownSelectToClose
                key={step.id}
                doSelect={() => !disabled && onStepChange(step.id)}
              >
                <button
                  type="button"
                  disabled={disabled}
                  className={`w-full flex flex-col items-start gap-0.5 py-2 px-3 rounded-md text-sm ${isActive
                    ? 'bg-primary/10 text-primary'
                    : disabled
                      ? 'opacity-40 cursor-not-allowed'
                      : 'text-onsurface hover:bg-surface-darker'
                    }`}
                >
                  <HeaderNavContent step={step} />
                </button>
              </DropDownSelectToClose>
            );
          })}
        </DropdownContent>
      </Dropdown>

      <PageContentCard className="flex-1 min-h-0 flex flex-col">
        {renderStepContent(activeStep)}
      </PageContentCard>
    </div>
  );
}

function HeaderNavContent({ step }: { step: Step }) {
  const { t } = useTranslation();
  
  return (
    <>
      <div className="text-sm font-medium truncate">{t(step.titleKey)}</div>
      <div className="text-xs opacity-60 mt-0.5 truncate">
        {step.getSubtitle() || ''}
      </div>
    </>
  );
}
